// Birim varyantı DR1/DR4/DR6/DR8/DR12 — IndicatorModal'ın "Birim — müşteri genelinde" bölümü.
// Birim set satırının alanı DEĞİL (müşteri × gösterge düzeyinde, ayrı eylem): ağaç taslağına
// yazılmaz, kendi "Kaydet ve N değeri çevir" düğmesiyle `applyKpiUnitChange` çağırır — diğer
// alanlar (etiket/konsolide/formül…) bildik taslak → üstteki Kaydet yolundan, birimden bağımsız.
// Akış çekirdeği `lib/kpi-unit-change-flow` (debounce + son istek kazanır + teyit metni + 409).
// Görünüm (`IndicatorUnitSectionView`) saf — SSR testleri durumu doğrudan verir.

import { useEffect, useMemo, useState } from "react";
import { isUnitTemplate, resolveUnitTemplate, type KpiSetIndicator } from "@mahir/shared";
import { KPI_UNIT_COPY } from "../../lib/kpi-unit-copy";
import {
  IDLE_UNIT_PREVIEW,
  createUnitPreviewScheduler,
  normalizeUnitPick,
  sameUnitPick,
  submitUnitChange,
  unitApplyBody,
  unitConfirmText,
  unitSaveButton,
  type UnitPreviewState,
} from "../../lib/kpi-unit-change-flow";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { UnitChangeBand, type UnitChangeMode } from "./UnitChangeBand";
import { UnitSelect } from "./UnitSelect";
import {
  btnPrimary,
  btnSmall,
  errorBox,
  fieldStyle,
  helpText,
  labelStyle,
} from "./kpi-form-styles";

const C = KPI_UNIT_COPY;

export interface CatalogUnitInfo {
  defaultUnit: string | null;
  unitVariants: readonly string[];
}

export interface IndicatorUnitApplied {
  metricId: string;
  /** etkin birim (müşteri kaydı ?? katalog varsayılanı) */
  unit: string | null;
  unitSource: "client" | "default";
  converted: number;
  targetsConverted: number;
  /** relabel'de `converted` = birimi düzeltilen satır sayısı (sayılar değişmedi) */
  mode: UnitChangeMode;
}

export interface IndicatorUnitActions {
  onOpenFormula?: (code: string, setId: string | null) => void;
  onInspectValues?: (code: string) => void;
  onInspectReported?: (code: string) => void;
  onInspectTargets?: (code: string) => void;
}

export interface IndicatorUnitSectionProps extends IndicatorUnitActions {
  indicator: KpiSetIndicator;
  clientId: string | null;
  clientName: string;
  /** katalog metriğinin varsayılan birimi + alternatifleri (yüklenmediyse null) */
  catalogUnit: CatalogUnitInfo | null;
  /** kod → birim (türetilmiş şablon çözümü) */
  setUnits?: ReadonlyMap<string, string | null>;
  disabled: boolean;
  onApplied: (r: IndicatorUnitApplied) => void;
  /** uygulanmamış birim seçimi var mı (modal "kaydedilmemiş" uyarısı) */
  onPendingChange?: (pending: boolean) => void;
}

/** Katalog varsayılanı: katalogdan; yoksa göstergenin kendi birimi (müşteri kaydı yoksa). */
export function indicatorDefaultUnit(
  ind: Pick<KpiSetIndicator, "unit" | "unitSource">,
  catalogUnit: CatalogUnitInfo | null,
): string | null {
  if (catalogUnit) return catalogUnit.defaultUnit;
  return ind.unitSource === "default" ? ind.unit : null;
}

export interface IndicatorUnitSectionViewProps extends IndicatorUnitActions {
  indicator: KpiSetIndicator;
  clientName: string;
  defaultUnit: string | null;
  variants: readonly string[];
  setUnits?: ReadonlyMap<string, string | null>;
  disabled: boolean;
  /** undefined = seçim yapılmadı (mevcut birim) */
  pick: string | null | undefined;
  preview: UnitPreviewState;
  mode: UnitChangeMode | null;
  saving: boolean;
  confirmText: string | null;
  confirmOpen: boolean;
  conflict: string | null;
  onPick: (unit: string | null) => void;
  onMode: (mode: UnitChangeMode) => void;
  onRecheck: () => void;
  onSave: () => void;
  onConfirm: () => void;
  onCancelConfirm: () => void;
}

export function IndicatorUnitSectionView(props: IndicatorUnitSectionViewProps): JSX.Element {
  const { indicator: ind, pick, preview: state, mode } = props;
  const current = ind.unitSource === "client" ? ind.unit : null;
  const changed = pick !== undefined && !sameUnitPick(pick, current);
  const button = unitSaveButton(state, mode, props.saving, props.conflict !== null);
  let derived: { unit: string | null; template: string | null } | null = null;
  if (ind.isDerived) {
    const units = props.setUnits;
    const r = resolveUnitTemplate(ind.unit, (code) => units?.get(code));
    derived = { unit: r.unit, template: isUnitTemplate(ind.unit) ? ind.unit : null };
  }

  return (
    <div style={fieldStyle}>
      <label style={labelStyle} htmlFor="ind-unit">
        {C.fieldLabel}
      </label>
      <span style={helpText}>{C.scopeLine(props.clientName)}</span>
      {derived ? (
        <UnitSelect
          id="ind-unit"
          value={current}
          defaultUnit={props.defaultUnit}
          variants={props.variants}
          current={ind.unit}
          derivedUnit={derived.unit}
          derivedTemplate={derived.template}
          onChange={() => undefined}
        />
      ) : (
        <>
          <UnitSelect
            id="ind-unit"
            value={pick !== undefined ? pick : current}
            defaultUnit={props.defaultUnit}
            variants={props.variants}
            current={ind.unit}
            disabled={props.disabled || props.saving}
            onChange={props.onPick}
          />
          {changed ? (
            <>
              <UnitChangeBand
                preview={state.preview}
                loading={state.loading}
                error={state.error}
                mode={mode}
                onMode={props.onMode}
                onRecheck={props.onRecheck}
                clientName={props.clientName}
                variant="indicator"
                indicator={{ code: ind.code, label: ind.label }}
                {...(props.onOpenFormula ? { onOpenFormula: props.onOpenFormula } : {})}
                {...(props.onInspectValues ? { onInspectValues: props.onInspectValues } : {})}
                {...(props.onInspectReported ? { onInspectReported: props.onInspectReported } : {})}
                {...(props.onInspectTargets ? { onInspectTargets: props.onInspectTargets } : {})}
              />
              {props.conflict ? (
                <div
                  role="alert"
                  style={{ ...errorBox, fontSize: "0.875rem", display: "grid", gap: 6 }}
                >
                  <span>{props.conflict}</span>
                  <span>
                    <button type="button" style={btnSmall} onClick={props.onRecheck}>
                      ↻ {C.band.recheck}
                    </button>
                  </span>
                </div>
              ) : null}
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  data-testid="kpi-unit-save"
                  style={{ ...btnPrimary, opacity: button.disabled ? 0.5 : 1 }}
                  disabled={button.disabled}
                  aria-busy={props.saving || undefined}
                  onClick={props.onSave}
                >
                  {button.label}
                </button>
              </div>
            </>
          ) : null}
        </>
      )}
      <ConfirmDialog
        open={props.confirmOpen && props.confirmText !== null}
        title={C.confirmTitle}
        body={props.confirmText ?? ""}
        confirmLabel={button.label}
        busy={props.saving}
        busyLabel={C.saving}
        onConfirm={props.onConfirm}
        onCancel={props.onCancelConfirm}
      />
    </div>
  );
}

export function IndicatorUnitSection(props: IndicatorUnitSectionProps): JSX.Element {
  const { indicator: ind, clientId, catalogUnit, onApplied, onPendingChange } = props;
  const metricId = ind.metricId;
  const defaultUnit = indicatorDefaultUnit(ind, catalogUnit);
  const current = ind.unitSource === "client" ? ind.unit : null;
  const [pick, setPick] = useState<string | null | undefined>(undefined);
  const [state, setState] = useState<UnitPreviewState>(IDLE_UNIT_PREVIEW);
  const [mode, setMode] = useState<UnitChangeMode | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [conflict, setConflict] = useState<string | null>(null);
  const scheduler = useMemo(
    () => (clientId ? createUnitPreviewScheduler({ clientId, metricId }, setState) : null),
    [clientId, metricId],
  );
  useEffect(() => () => scheduler?.cancel(), [scheduler]);

  const changed = pick !== undefined && !sameUnitPick(pick, current);
  useEffect(() => {
    onPendingChange?.(changed);
  }, [changed, onPendingChange]);

  const target = pick ?? null;
  const unitLabel = target ?? defaultUnit ?? C.unitless;
  const confirmText = state.preview ? unitConfirmText(state.preview, mode, unitLabel) : null;

  function handlePick(raw: string | null): void {
    const u = normalizeUnitPick(raw, defaultUnit);
    setPick(u);
    setMode(null);
    setConflict(null);
    if (sameUnitPick(u, current)) scheduler?.cancel();
    else scheduler?.schedule(u);
  }

  async function apply(): Promise<void> {
    if (!clientId || !state.preview) return;
    setSaving(true);
    const out = await submitUnitChange(
      unitApplyBody({ clientId, metricId }, target, state.preview, mode),
    );
    setSaving(false);
    setConfirmOpen(false);
    if (!out.ok) {
      setConflict(out.message);
      return;
    }
    setPick(undefined);
    onApplied({
      metricId,
      unit: out.result.unit ?? defaultUnit,
      unitSource: out.result.unit === null ? "default" : "client",
      converted: out.result.converted,
      targetsConverted: out.result.targetsConverted,
      mode: out.result.mode,
    });
  }

  return (
    <IndicatorUnitSectionView
      indicator={ind}
      clientName={props.clientName}
      defaultUnit={defaultUnit}
      variants={catalogUnit?.unitVariants ?? []}
      {...(props.setUnits ? { setUnits: props.setUnits } : {})}
      disabled={props.disabled || !clientId}
      pick={pick}
      preview={state}
      mode={mode}
      saving={saving}
      confirmText={confirmText}
      confirmOpen={confirmOpen}
      conflict={conflict}
      onPick={handlePick}
      onMode={setMode}
      onRecheck={() => {
        setConflict(null);
        setMode(null);
        scheduler?.refresh(target);
      }}
      onSave={() => {
        if (confirmText) setConfirmOpen(true);
        else void apply();
      }}
      onConfirm={() => void apply()}
      onCancelConfirm={() => {
        if (!saving) setConfirmOpen(false);
      }}
      {...(props.onOpenFormula ? { onOpenFormula: props.onOpenFormula } : {})}
      {...(props.onInspectValues ? { onInspectValues: props.onInspectValues } : {})}
      {...(props.onInspectReported ? { onInspectReported: props.onInspectReported } : {})}
      {...(props.onInspectTargets ? { onInspectTargets: props.onInspectTargets } : {})}
    />
  );
}
